/**
 * Sidebar — left-rail navigation for the ops dashboard.
 *
 * Grouped IA (console-home-cockpit): instead of a flat list that had grown to
 * 13 operator / 18 staff items, screens are grouped into a handful of AREAS:
 *
 *   Home · Payments (Transactions/Disputes/Accounting) · Location (Zones/QR/
 *   Permits/Validations/Tenants/Enforcement) · Analytics · Settings
 *
 * plus a visually separated "Parkify internal" block for staff-only tools.
 * Groups are PRESENTATION ONLY — every screen keeps its id, URL, permission
 * and feature-flag gates; App.tsx filters children exactly as before and a
 * group with zero visible children never renders.
 *
 * Interaction:
 *   - A group with children expands/collapses (chevron). Clicking its header
 *     also navigates to its first visible child so the click is never dead.
 *   - The group containing the current screen auto-expands (deep links land
 *     with the right section open).
 *   - Collapsed rail (56px) shows one icon per group; clicking navigates to
 *     the group's first child.
 */

import type { GlassChromePlacement } from '@parkify/ui/GlassSurface'
import { useEffect, useMemo, useState, type ReactNode } from 'react'
import RailTooltip from './RailTooltip'

/** Desktop overlay widths. The content inset is the open/collapsed width minus the peek. */
export const OPS_RAIL_OPEN = 220
export const OPS_RAIL_COLLAPSED = 56
export const OPS_RAIL_PEEK = 14
// Same 16px the mobile shell uses as its page inset. Sits outside the rail's
// outer edge in both desktop states so copy is not flush with the chrome.
export const OPS_RAIL_CONTENT_GAP = 16

/**
 * Nav icon box. The glyphs are 24-grid line icons that sat in a 16px box inside
 * a 36px row, which read as undersized next to the 13px labels (Austin,
 * 2026-10-20). Every NavGroup icon is stretched to this box by `.ops-nav-icon`
 * in index.css, so a new icon needs no size of its own.
 */
export const OPS_NAV_ICON_SIZE = 21

/**
 * Odd sizes on purpose. The collapsed rail's content box is 55px wide (56 less
 * the 1px border), centre 27.5. Chrome snaps an SVG's box to whole pixels, so
 * an even-sized icon centred on 27.5 starts on a half pixel and is painted
 * 0.5px right of centre (measured: the 20px icons and the 16px toggle, which
 * sat at 28.0). An odd size starts on a whole pixel and paints on 27.5.
 */
export const OPS_TOGGLE_ICON_SIZE = 17

/**
 * The Stack's ink box inside the released logo (parkify-logo.svg, viewBox
 * 0 0 129.21 30.85), measured from the path geometry: x 93.02..124.13,
 * y 4.45..26.60. public/parkify-stack-purple.svg is cropped to exactly this box,
 * so the image box IS the ink box and flex centering is true centering. The
 * Stack is mirror-symmetric about its vertical axis, so its horizontal ink
 * centroid sits on the box centre: the collapsed mark was "off-centre" because
 * the old mark was a cover-crop of the whole lockup, not because the box and
 * the visual centre disagree.
 */
export const OPS_STACK_ASPECT = 31.11 / 22.15

/**
 * Collapsed rail: the Stack alone. Brand rules §1: clear space is the dot of the
 * lowercase "i", 5.1 units against the Stack's 31.11, i.e. 16.4% of the Stack's
 * width per side (6.1px at this width). #4170 set 39px; Austin (2026-10-21) found
 * the logo "a tad too big" and asked for 5-10% off, so 37px (-5.1%). It stays odd
 * on purpose: the 55px content box leaves (55 - 37) / 2 = 9px each side, a whole
 * pixel, so the Stack still paints on the rail centreline (see OPS_TOGGLE_ICON_SIZE).
 * 9px each side is 1.5x the clear space the brand rules require.
 */
export const OPS_RAIL_STACK_WIDTH = 37

/**
 * Expanded header lockup height (#4170 set 32; -6.25% per Austin's 5-10% ask).
 * The 220px rail leaves room for the toggle beside it.
 */
export const OPS_LOGO_HEIGHT = 30

/**
 * Collapsed-rail breathing room (Austin, 2026-10-21: "squeezed too tight, could
 * use some more padding"). Nav rows are inset 10px from the rail edge (was 8),
 * so the hover pill is 35px wide and the icon, which stays at OPS_NAV_ICON_SIZE,
 * has 7px of pill on each side; rows are 39px tall (was 37) and 4px apart (was
 * 2); the Stack gets 20px above and 14px below (was 16 and 12). 35 - 21 = 14 is
 * even, so the icon still starts on a whole pixel.
 */
export const OPS_RAIL_NAV_PAD_X = 10
export const OPS_RAIL_NAV_ROW_PAD_Y = 9
export const OPS_RAIL_NAV_GAP = 4

export const PARKIFY_LOGO_COLOR = '/parkify-logo-color.svg'
export const PARKIFY_STACK_PURPLE = '/parkify-stack-purple.svg'

/** The Stack alone, in Master Purple, at `width` px (height follows the ink box). */
function ParkifyStack({ width, alt }: { width: number; alt: string }) {
  return (
    <img
      src={PARKIFY_STACK_PURPLE}
      alt={alt}
      width={width}
      height={width / OPS_STACK_ASPECT}
      style={{ display: 'block', width, height: width / OPS_STACK_ASPECT }}
    />
  )
}

/** Neutral labels and a tinted selection match the demo's white console rail. */
const RAIL_LABEL = 'var(--color-gray-600)'
const RAIL_ACTIVE = 'var(--parkify-accent-strong)'
const RAIL_SELECTED = 'color-mix(in srgb, var(--parkify-accent) 12%, white)'

export interface NavItem {
  id: string
  label: string
  icon: ReactNode
  badge?: number | null
}

export interface NavGroup {
  id: string
  label: string
  icon: ReactNode
  /** Empty → the group is a leaf (Home, Analytics): clicking navigates to `id`. */
  children: NavItem[]
  /** Staff-only block, rendered after a divider under a PARKIFY INTERNAL label. */
  internal?: boolean
}

interface SidebarProps {
  groups: NavGroup[]
  currentId: string
  onSelect: (id: string) => void
  open: boolean
  onToggle: () => void
  /** Desktop overlay rail, or the mobile hamburger drawer. */
  placement?: GlassChromePlacement
  /** Mobile drawer slid off-screen. Transform stays on the glass element. */
  offscreen?: boolean
  userEmail?: string | null
  onSignOut?: () => void
  /**
   * Set while a View as lens or a client session is open. The footer then says
   * whose console this is and offers the way back, ahead of Sign out: the
   * 2026-09-25 report was a sidebar showing only the client user's email over
   * "Sign out", which read as being signed in as them.
   */
  lens?: { label: string; exitLabel: string; onExit: () => void }
  /** Clicking the brand mark navigates here — the console's home screen. */
  onHome?: () => void
  // Optional tenant branding — when set, swaps the sidebar logo for the
  // tenant's mark. Otherwise falls back to the Parkify logo.
  clientLogoUrl?: string
  clientIconUrl?: string
  clientName?: string
  /**
   * Set for a person who belongs to two or more clients (api
   * auth/switch_client.ts). The footer offers the same "Viewing as"-style
   * picker staff get in Client Admin; picking one re-issues their session for
   * that client. Never set under a lens.
   */
  clientSwitcher?: {
    currentId: string
    options: Array<{ clientId: string; name: string }>
    onSwitch: (clientId: string) => void
  }
}

// The screen a group click lands on: itself for a leaf, else its first child.
function groupTarget(g: NavGroup): string {
  return g.children.length > 0 ? g.children[0]!.id : g.id
}

export default function Sidebar({
  groups, currentId, onSelect, open, onToggle, userEmail, onSignOut, onHome,
  clientLogoUrl, clientIconUrl, clientName, lens, clientSwitcher,
  placement = 'rail', offscreen = false,
}: SidebarProps) {
  // A tenant with its own logo means the console is white-labeled — surface a
  // tasteful "Powered by Parkify" so attribution stays clear.
  const whiteLabeled = Boolean(clientLogoUrl)

  // Group whose child list contains the current screen (undefined for leaves).
  const activeGroupId = useMemo(
    () => groups.find(g => g.children.some(c => c.id === currentId))?.id,
    [groups, currentId],
  )

  // Expanded state. Starts with the active group open; navigating into a
  // group (deep link, quick link, perm bounce) re-opens it. Manual toggles
  // stick for the rest — a Set so several groups can sit open at once.
  const [expanded, setExpanded] = useState<Set<string>>(
    () => new Set(activeGroupId ? [activeGroupId] : []),
  )
  useEffect(() => {
    if (!activeGroupId) return
    setExpanded(prev => {
      if (prev.has(activeGroupId)) return prev
      const next = new Set(prev)
      next.add(activeGroupId)
      return next
    })
  }, [activeGroupId])

  const toggleGroup = (id: string) => setExpanded(prev => {
    const next = new Set(prev)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    return next
  })

  const firstInternalId = groups.find(g => g.internal)?.id

  return (
    <aside style={{
      boxSizing: 'border-box',
      background: 'var(--color-white)',
      borderRight: '1px solid var(--color-gray-200)',
      position: 'fixed',
      top: 0,
      left: 0,
      zIndex: 1000,
      width: placement === 'drawer' || open ? OPS_RAIL_OPEN : OPS_RAIL_COLLAPSED,
      height: '100vh',
      maxHeight: '100dvh',
      color: 'var(--color-black)',
      display: 'flex',
      flexDirection: 'column',
      paddingRight: placement === 'rail' && open ? OPS_RAIL_PEEK : 0,
      // The drawer sits over white content, so it needs the elevation the glass
      // chrome gave it. Not while slid off-screen: the shadow would show at the edge.
      boxShadow: placement === 'drawer' && !offscreen
        ? '0 12px 32px rgba(35,31,32,0.10), 0 2px 8px rgba(35,31,32,0.05)'
        : 'none',
      transition: placement === 'drawer' ? 'transform 200ms ease' : 'width 180ms ease',
      transform: placement === 'drawer' && offscreen ? 'translateX(-100%)' : 'none',
    }}>
      <div style={{ padding: open ? '18px 16px' : '20px 0 14px', display: 'flex', alignItems: 'center', justifyContent: open ? 'space-between' : 'center' }}>
        {/* The brand mark is the console's home affordance — the whole mark is
            tappable (not just a hit-box around the glyph) and lands on Home,
            matching what every other web console does with its logo. Kept a
            <button> so it is keyboard-reachable and announced. */}
        <RailTooltip label="Parkify console home" enabled={!open}>
        <button
          onClick={onHome}
          aria-label="Parkify console home"
          style={{
            display: 'flex', alignItems: 'center', padding: 0, border: 'none',
            background: 'none', cursor: 'pointer',
          }}
        >
        {open ? (
          clientLogoUrl ? (
            // Tenant logo — bounded to keep tall logos from blowing out the
            // sidebar header. Falls through to the Parkify mark if loading
            // fails (broken URL, CORS issue on the tenant's CDN).
            <img
              src={clientLogoUrl}
              alt={clientName ?? 'Client'}
              style={{ maxHeight: 28, maxWidth: 160, objectFit: 'contain', display: 'block' }}
              onError={e => { (e.currentTarget as HTMLImageElement).src = PARKIFY_LOGO_COLOR }}
            />
          ) : (
            // Full-color master: Master Black wordmark, Master Purple Stack.
            <img
              src={PARKIFY_LOGO_COLOR}
              alt="Parkify"
              style={{ height: OPS_LOGO_HEIGHT, width: 'auto', display: 'block' }}
            />
          )
        ) : clientIconUrl ? (
          // Tenant icon — falls back to the Parkify Stack if it fails to load.
          <img
            src={clientIconUrl}
            alt={clientName ?? 'Parkify'}
            style={{ height: 26, width: 26, objectFit: 'contain', borderRadius: 6, display: 'block' }}
            onError={e => {
              const img = e.currentTarget as HTMLImageElement
              img.onerror = null
              img.src = PARKIFY_STACK_PURPLE
              img.style.objectFit = 'fill'
              img.style.borderRadius = '0'
              img.style.width = `${OPS_RAIL_STACK_WIDTH}px`
              img.style.height = `${OPS_RAIL_STACK_WIDTH / OPS_STACK_ASPECT}px`
            }}
          />
        ) : (
          // Collapsed: the Stack alone, no wordmark. Brand rules §1: the Stack may
          // appear alone. public/parkify-stack-purple.svg is the released logo's
          // Stack paths, sliced verbatim and recolored, cropped to its ink box.
          <ParkifyStack width={OPS_RAIL_STACK_WIDTH} alt={clientName ?? 'Parkify'} />
        )}
        </button>
        </RailTooltip>
        {open && (
          <button onClick={onToggle} style={{ color: RAIL_LABEL }} aria-label="Toggle sidebar">
            <svg width={OPS_TOGGLE_ICON_SIZE} height={OPS_TOGGLE_ICON_SIZE} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
              <line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="18" x2="21" y2="18"/>
            </svg>
          </button>
        )}
      </div>
      {!open && (
        <RailTooltip label="Expand sidebar">
        <button
          onClick={onToggle}
          style={{ color: RAIL_LABEL, padding: '4px 0 8px', display: 'flex', justifyContent: 'center' }}
          aria-label="Toggle sidebar"
        >
          <svg width={OPS_TOGGLE_ICON_SIZE} height={OPS_TOGGLE_ICON_SIZE} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
            <line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="18" x2="21" y2="18"/>
          </svg>
        </button>
        </RailTooltip>
      )}

      <nav style={{
        flex: 1, padding: open ? '4px 8px' : `4px ${OPS_RAIL_NAV_PAD_X}px`, display: 'flex', flexDirection: 'column',
        gap: open ? 2 : OPS_RAIL_NAV_GAP, overflowY: 'auto',
      }}>
        {groups.map(group => {
          const isLeaf = group.children.length === 0
          const isExpanded = expanded.has(group.id)
          const childActive = group.children.some(c => c.id === currentId)
          // A leaf is active on its own screen; a parent header lights up only
          // while collapsed (expanded → the active CHILD row carries the highlight).
          const headerActive = isLeaf ? currentId === group.id : (childActive && (!isExpanded || !open))

          return (
            <div key={group.id}>
              {/* Divider + label ahead of the first Parkify-internal group. */}
              {group.id === firstInternalId && (
                <div style={{ margin: '10px 2px 4px', borderTop: '1px solid var(--color-gray-200)', paddingTop: 8 }}>
                  {open && (
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 6, padding: '0 10px 4px',
                      fontSize: 9, fontWeight: 800, letterSpacing: '2px', color: RAIL_LABEL,
                    }}>
                      <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
                        <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                      </svg>
                      PARKIFY INTERNAL
                    </div>
                  )}
                </div>
              )}
              <RailTooltip label={group.label} enabled={!open}>
              <button
                onClick={() => {
                  if (isLeaf) { onSelect(group.id); return }
                  // Parent click: land on the first child; keep the section open.
                  // Collapsed rail can't show children, so it just navigates.
                  if (open) setExpanded(prev => new Set(prev).add(group.id))
                  onSelect(groupTarget(group))
                }}
                aria-expanded={isLeaf ? undefined : isExpanded}
                // Collapsed rows are icon-only, so the label is their only accessible name.
                aria-label={open ? undefined : group.label}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                  // Collapsed: one icon per row, centred in the row so the icons,
                  // the Stack above and the toggle all share the rail's centreline.
                  justifyContent: open ? undefined : 'center',
                  padding: open ? '8px 10px' : `${OPS_RAIL_NAV_ROW_PAD_Y}px 0`, borderRadius: 8,
                  background: headerActive ? RAIL_SELECTED : 'transparent',
                  color: headerActive ? RAIL_ACTIVE : RAIL_LABEL,
                  fontWeight: headerActive || !isLeaf ? 700 : 500, fontSize: 13,
                }}
                onMouseEnter={e => { if (!headerActive) (e.currentTarget as HTMLElement).style.background = 'var(--color-gray-100)' }}
                onMouseLeave={e => { if (!headerActive) (e.currentTarget as HTMLElement).style.background = 'transparent' }}
              >
                <span className="ops-nav-icon" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, width: OPS_NAV_ICON_SIZE, height: OPS_NAV_ICON_SIZE }}>{group.icon}</span>
                {open && <span style={{ flex: 1, textAlign: 'left' }}>{group.label}</span>}
                {open && !isLeaf && (
                  <span
                    role="button"
                    aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${group.label}`}
                    onClick={e => { e.stopPropagation(); toggleGroup(group.id) }}
                    style={{ display: 'flex', alignItems: 'center', padding: 2, opacity: 0.55 }}
                  >
                    <svg
                      width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                      strokeWidth="2.4" strokeLinecap="round"
                      style={{ transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform 140ms ease' }}
                    >
                      <polyline points="6 9 12 15 18 9"/>
                    </svg>
                  </span>
                )}
              </button>
              </RailTooltip>
              {open && !isLeaf && isExpanded && (
                <div style={{ padding: '1px 0 3px 22px', display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {group.children.map(item => {
                    const active = currentId === item.id
                    return (
                      <button
                        key={item.id}
                        onClick={() => onSelect(item.id)}
                        style={{
                          width: '100%', display: 'flex', alignItems: 'center', gap: 8,
                          padding: '6px 10px', borderRadius: 7,
                          borderLeft: '1px solid var(--color-gray-200)',
                          background: active ? RAIL_SELECTED : 'transparent',
                          color: active ? RAIL_ACTIVE : RAIL_LABEL,
                          fontWeight: active ? 700 : 500, fontSize: 12.5,
                        }}
                        onMouseEnter={e => { if (!active) (e.currentTarget as HTMLElement).style.background = 'var(--color-gray-100)' }}
                        onMouseLeave={e => { if (!active) (e.currentTarget as HTMLElement).style.background = 'transparent' }}
                      >
                        <span style={{ flex: 1, textAlign: 'left' }}>{item.label}</span>
                        {item.badge != null && (
                          <span style={{ fontSize: 10, fontWeight: 800, padding: '1px 6px', borderRadius: 10, background: 'var(--parkify-accent-strong)', color: 'var(--color-white)' }}>
                            {item.badge}
                          </span>
                        )}
                      </button>
                    )
                  })}
                </div>
              )}
            </div>
          )
        })}
      </nav>

      {userEmail && (
        <div style={{ padding: '12px', borderTop: '1px solid var(--color-gray-200)' }}>
          {lens && (open ? (
            <div style={{
              marginBottom: 10, padding: '8px 10px', borderRadius: 8,
              background: 'rgba(245,158,11,0.14)', border: '1px solid rgba(245,158,11,0.45)',
            }}>
              {/* #92400e on the 14% amber tint over white: 6.36:1 (AA for 11px text). */}
              <div style={{ fontSize: 11, color: '#92400e', fontWeight: 700, marginBottom: 6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {lens.label}
              </div>
              <button
                onClick={lens.onExit}
                style={{
                  width: '100%', padding: '6px 8px', borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit',
                  fontSize: 11, fontWeight: 700, color: '#231F20', background: '#fbbf24', border: 'none',
                }}
              >
                {lens.exitLabel}
              </button>
            </div>
          ) : (
            <button onClick={lens.onExit} aria-label={lens.exitLabel} title={lens.label}
                    style={{ color: '#92400e', display: 'block', marginBottom: 10 }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" /><circle cx="12" cy="12" r="3" />
              </svg>
            </button>
          ))}
          {open && clientSwitcher && clientSwitcher.options.length > 1 && (
            <label style={{ display: 'block', marginBottom: 10 }}>
              <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.5px', textTransform: 'uppercase', color: RAIL_LABEL, marginBottom: 4 }}>Client</div>
              <select
                aria-label="Switch client"
                value={clientSwitcher.currentId}
                onChange={e => { if (e.target.value !== clientSwitcher.currentId) clientSwitcher.onSwitch(e.target.value) }}
                style={{ width: '100%', padding: '6px 8px', borderRadius: 8, border: '1.5px solid var(--color-gray-300)', fontSize: 12, fontFamily: 'inherit', background: 'var(--color-white)' }}
              >
                {clientSwitcher.options.map(o => <option key={o.clientId} value={o.clientId}>{o.name}</option>)}
              </select>
            </label>
          )}
          {open ? (
            <>
              {/* No role pill. Permissions here are granular per product and per
                  feature, so one word ("Manager") is a job title dressed up as a
                  permission set — it reads as authoritative while being unable to
                  tell you what the person can actually do. Settings → Team is
                  where the real answer lives. */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                <div style={{ fontSize: 11, color: RAIL_LABEL, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                  {userEmail}
                </div>
              </div>
              <button
                onClick={onSignOut}
                style={{ fontSize: 11, color: RAIL_LABEL, textDecoration: 'underline' }}
              >
                Sign out
              </button>
            </>
          ) : (
            <RailTooltip label="Sign out">
              <button onClick={onSignOut} aria-label="Sign out" style={{ color: RAIL_LABEL }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
              </button>
            </RailTooltip>
          )}
        </div>
      )}

      {/* White-label attribution — only when the tenant has its own branding. */}
      {whiteLabeled && (
        <div style={{
          padding: open ? '10px 14px 14px' : '10px 0 14px',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          gap: 6,
        }}>
          {open && (
            <span style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.3px', color: RAIL_LABEL }}>
              Powered by
            </span>
          )}
          {open ? (
            <img src={PARKIFY_LOGO_COLOR} alt="Powered by Parkify"
              style={{ height: 13, width: 'auto', display: 'block' }} />
          ) : (
            // Collapsed: the Stack alone (see the rail mark above).
            <ParkifyStack width={24} alt="Powered by Parkify" />
          )}
        </div>
      )}
    </aside>
  )
}
